// src/components/admin/GestionResultadosAtencion.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

// Catálogo editable de resultados de la atención — mismo patrón que GestionMotivosRechazo.
// El nombre que se carga acá es exactamente el valor que va a guardar el turno al finalizar
// la atención (ver TurnoRecepcionEntity.resultadoAtencion) y el que van a mostrar los
// reportes de Recepción. Ver la migración V224, que creó el catálogo y convirtió los códigos
// viejos (INICIA_AHORA, CARPETA_FUTURO, NO_VIABLE) a los nombres sembrados.
const GestionResultadosAtencion = () => {
    const [resultados, setResultados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedResultado, setSelectedResultado] = useState(null);
    const [nombre, setNombre] = useState('');
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    const [filterNombre, setFilterNombre] = useState('');

    useEffect(() => {
        cargarResultados();
    }, []);

    const cargarResultados = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getResultadosAtencion();
            setResultados(response.data);
        } catch {
            mostrarAlerta('Error al cargar los resultados de la atención', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, resultado = null) => {
        setModalMode(mode);
        if (mode === 'edit' && resultado) {
            setSelectedResultado(resultado);
            setNombre(resultado.nombre);
        } else {
            setSelectedResultado(null);
            setNombre('');
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedResultado(null);
        setNombre('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createResultadoAtencion({ nombre });
                mostrarAlerta('Resultado de atención creado exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateResultadoAtencion(selectedResultado.id, { nombre });
                mostrarAlerta('Resultado de atención actualizado exitosamente', 'success');
            }
            handleCloseModal();
            cargarResultados();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al guardar el resultado de la atención', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este resultado? No afecta a los turnos que ya lo tienen cargado.')) return;
        setLoading(true);
        try {
            await DatosMaestrosService.deleteResultadoAtencion(id);
            mostrarAlerta('Resultado de atención eliminado exitosamente', 'success');
            cargarResultados();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al eliminar el resultado de la atención', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const resultadosFiltrados = resultados.filter(m =>
        coincideBusqueda(m.nombre, filterNombre)
    );

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <Button variant="primary" onClick={() => handleOpenModal('create')}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Nuevo Resultado
                </Button>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${resultadosFiltrados.length} de ${resultados.length} resultados`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-resultado-nombre" ancho>
                    <Form.Control
                        id="filtro-resultado-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre del resultado…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && resultados.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {resultadosFiltrados.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center text-muted py-4">
                                    {resultados.length === 0
                                        ? "No hay resultados de la atención registrados"
                                        : "No se encontraron resultados que coincidan con la búsqueda"}
                                </td>
                            </tr>
                        ) : (
                            resultadosFiltrados.map((resultado) => (
                                <tr key={resultado.id}>
                                    <td className="fw-semibold">{resultado.nombre}</td>
                                    <td className="text-end">
                                        <MenuAccionesFila acciones={[
                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', resultado) },
                                            { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(resultado.id) },
                                        ]} />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nuevo Resultado de la Atención' : 'Editar Resultado de la Atención'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                placeholder="Ej: Derivado a otra área"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default GestionResultadosAtencion;
